import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import bookingService, { Booking } from "../../services/bookingService";
import { BOOKING_STATUS } from "../../constants/bookingStatus";
import { ScrollNavbar, BookingCard } from "../../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faSpinner,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

type Category = "upcoming" | "past" | "all";

const Bookings = () => {
  const { user } = useAuth();
  const history = useHistory();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category>("upcoming");

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }

    const fetchBookings = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await bookingService.getAllBookingsForCustomer(
          String(user.id)
        );
        if (response.success && response.data) {
          // Sort by most recent first
          const sortedBookings = response.data.sort((a, b) => {
            const dateA = new Date(a.pickup_date).getTime();
            const dateB = new Date(b.pickup_date).getTime();
            if (dateB < dateA) {
              return -1;
            }
            if (dateB > dateA) {
              return 1;
            }
            return 0;
          });
          setBookings(sortedBookings);
        } else {
          setError(response.message || "Failed to fetch bookings.");
          setBookings([]);
        }
      } catch (err: any) {
        setError(
          err.message || "An unexpected error occurred while fetching bookings."
        );
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, [user, history]);

  const filterBookings = (category: Category): Booking[] => {
    const now = new Date();
    switch (category) {
      case "upcoming":
        return bookings.filter(
          (b) =>
            new Date(b.pickup_date) >= now &&
            b.booking_status !== BOOKING_STATUS.CANCELLED
        );
      case "past":
        return bookings.filter(
          (b) =>
            new Date(b.pickup_date) < now ||
            b.booking_status === BOOKING_STATUS.CANCELLED
        );
      case "all":
      default:
        return bookings;
    }
  };

  const filteredBookings = filterBookings(activeCategory);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-20">
          <FontAwesomeIcon
            icon={faSpinner}
            className="text-4xl text-blue-600 animate-spin"
          />
          <p className="mt-4 text-lg text-gray-600">Loading bookings...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-20 bg-red-50 p-6 rounded-lg">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-4xl text-red-500"
          />
          <p className="mt-4 text-lg text-red-700">Error: {error}</p>
        </div>
      );
    }

    if (filteredBookings.length === 0) {
      return (
        <div className="text-center py-20">
          <FontAwesomeIcon
            icon={faBoxOpen}
            className="text-5xl text-gray-400"
          />
          <p className="mt-4 text-xl text-gray-600">
            No {activeCategory} bookings found.
          </p>
          {activeCategory === "upcoming" && (
            <button
              onClick={() => history.push("/dashboard")}
              className="mt-4 bg-blue-600 text-white font-medium py-2 px-6 rounded-md shadow-md hover:bg-blue-700"
            >
              Book a Ride
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {filteredBookings.map((booking) => (
          <BookingCard
            key={booking.booking_id}
            booking={booking}
            onClick={() => history.push(`/bookings/${booking.booking_id}`)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ScrollNavbar />
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-mazzardbold text-gray-800 mb-8">
          My Bookings
        </h1>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex border-b border-gray-200">
            {(["upcoming", "past", "all"] as Category[]).map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-1 py-4 px-2 text-center font-medium capitalize text-sm sm:text-base transition-colors duration-200 ${
                  activeCategory === cat
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                {cat} Bookings
              </button>
            ))}
          </div>

          <div className="p-4 sm:p-6">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default Bookings;
